/* eslint-disable max-lines */
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    Button
} from 'shared/components/ui/button';
import {
    Edit, Save, X, Trash2
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'shared/components/ui/tooltip';
import {
    COLUMN_CONFIGS, formatFieldValue
} from './constants';
import {
    FieldEditor
} from './FieldEditor';
import {
 Field
} from './types/builderFields.types';
interface FieldsTableProps {
    processedFields: Field[];
    selectedFields: Field[];
    handleSelectField: (fieldId: string) => void;
    editingFields: string[]; // Array of field IDs currently being edited
    toggleEditingField: (fieldId: string) => void;
    handleSaveField: (field: Field) => void;
    handleDeleteField: (fields: Field[]) => void;
    handleFieldNameChange: (fieldId: string, newName: string) => void;
    nameError: string | null;
    setFields: React.Dispatch<React.SetStateAction<Field[]>>;
    tableColumns: { value: string; label: string }[];
    handleTableSelect: (selectedTable: string) => void;
    tables: { id: string; name: string }[];
    isLoading: boolean;
    hasNewFields: boolean;
    isSaving: boolean;
}

const formatColumnName = (text: string): string => {
    return text
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)(?=[A-Z][a-z])/g, '$1 ')
        .replace(/([A-Z]{2,})(?=\s|$)/g, '$1 ')
        .replace(/^./, (str) => str.toUpperCase())
        .replace(/\s+/g, ' ').trim();
};

const defaultFields = [
    'recordId',
    'createdOn',
    'modifiedOn',
    'createdBy',
    'modifiedBy',
    'recordStatus',
];

export const FieldsTable: React.FC<FieldsTableProps> = ({
    processedFields,
    selectedFields,
    handleSelectField,
    editingFields,
    toggleEditingField,
    handleSaveField,
    handleDeleteField,
    handleFieldNameChange,
    nameError,
    setFields,
    tableColumns,
    handleTableSelect,
    tables,
    isLoading,
    hasNewFields,
    isSaving,
}) => {
    const viewModeHiddenColumns = [
        'length',
        'format',
        'options',
        'default',
        'references',
        'equationFormula',
        'validation',
    ];

    const columnsToHide = hasNewFields || editingFields.length > 0 ? [] : viewModeHiddenColumns;

    return (
        <Table className="w-full relative">
            <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                    <TableHead className="w-[50px] p-0">{/* Select All checkbox? */}</TableHead>
                    {COLUMN_CONFIGS.map(
                        (config) =>
                            !columnsToHide.includes(config.id) && (
                                <TableHead key={config.id}>
                                    {formatColumnName(config.label)}
                                </TableHead>
                            )
                    )}
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {isLoading ? (
                    <TableRow>
                        <TableCell
                            colSpan={COLUMN_CONFIGS.length + 2 - columnsToHide.length}
                            className="h-24 text-center text-muted-foreground"
                        >
                            Loading fields...
                        </TableCell>
                    </TableRow>
                ) : processedFields.length === 0 ? (
                    <TableRow>
                        <TableCell
                            colSpan={COLUMN_CONFIGS.length + 2 - columnsToHide.length}
                            className="h-24 text-center text-muted-foreground"
                        >
                            No fields found.
                        </TableCell>
                    </TableRow>
                ) : (
                    processedFields.map((field) => {
                        const isEditing = editingFields.includes(field.id);
                        return (
                            <TableRow
                                key={field?.id}
                                className="group hover:bg-muted/30 h-12 align-middle"
                            >
                                <TableCell className="w-[50px] p-4">
                                    <div className="flex items-center justify-center h-full">
                                        {!defaultFields.includes(field.columnName) && !field.isNew && (
                                            <Checkbox
                                                checked={selectedFields.some(
                                                    (selected) => selected.id === field.id
                                                )}
                                                onCheckedChange={() => handleSelectField(field.id)}
                                                aria-label={`Select field ${field?.columnName}`}
                                            />
                                        )}
                                    </div>
                                </TableCell>
                                {COLUMN_CONFIGS.map(
                                    (config) =>
                                        !columnsToHide.includes(config.id) && (
                                            <TableCell
                                                key={config.id}
                                                className={`min-w-[200px] max-w-[400px] px-4 py-2 ${
                                                    isEditing && !defaultFields.includes(config.id)
                                                        ? ''
                                                        : 'max-h-[48px] overflow-hidden truncate whitespace-nowrap'
                                                }`}
                                            >
                                                {isEditing && !defaultFields.includes(config.id) ? (
                                                    <div className="whitespace-normal break-words">
                                                        <FieldEditor
                                                            field={field}
                                                            config={config}
                                                            setFields={setFields}
                                                            handleFieldNameChange={handleFieldNameChange}
                                                            nameError={nameError}
                                                            tableColumns={tableColumns}
                                                            handleTableSelect={handleTableSelect}
                                                            tables={tables}
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        {config.id === 'columnName' && (
                                                            <div className="truncate">{formatColumnName(field?.columnName)}</div>
                                                        )}

                                                        {config.id !== 'columnName' && (
                                                            <div>{formatFieldValue(field?.[config.id], config.id, {
 ...field,
validationType: field.validationType || ''
})}</div>
                                                        )}
                                                    </>
                                                )}
                                            </TableCell>
                                        )
                                )}
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end space-x-1">
                                        {!isSaving && !hasNewFields && (
                                            <>
                                                {!defaultFields.includes(field.columnName) && (
                                                    <>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-muted-foreground"
                                                                        onClick={() => {
                                                                            if (isEditing) {
                                                                                handleSaveField(field);
                                                                                toggleEditingField(field.id);
                                                                            } else {
                                                                                toggleEditingField(field.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isEditing ? (
                                                                            <Save className="h-4 w-4" />
                                                                        ) : (
                                                                            <Edit className="h-4 w-4" />
                                                                        )}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {isEditing ? 'Save changes' : 'Edit field'}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        {isEditing && (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:opacity-100 text-muted-foreground"
                                                                            onClick={() => toggleEditingField(field.id)}
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Cancel edit</TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                        onClick={() => {
                                                            if (!defaultFields.includes(field.columnName)) {
                                                                handleDeleteField([field]);
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {defaultFields.includes(field.columnName)
                                                        ? 'Cannot delete default field'
                                                        : 'Delete field'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })
                )}
            </TableBody>
        </Table>
    );
};
